import type { PrimitiveType } from '@accesso/cc-std-lib';
import type { Provider } from '@angular/core';
import { isPlatformServer, PlatformLocation } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';

import { keys, LoggerService, WINDOW } from '@accesso/cc-std-lib';

import type { StorageKey } from './storage.keys';
import type { ComplexPrimitiveObject, IStorageService, PrimitiveArray, PrimitiveObject } from './storage.model';
import { getStore } from '../../core/initializer/data.initializer';
import { StorageService } from './storage.token';

@Injectable({
	providedIn: 'root',
})
export class LocalStorageService implements IStorageService {
	private readonly window: Window = inject(WINDOW);
	private readonly logger = inject(LoggerService);
	/**
	 * If local storage is supported
	 */
	readonly isSupported: boolean;
	/**
	 * Fallback storage in the event we have no local storage
	 */
	private fallbackStorage: Map<string, unknown> = new Map<string, unknown>();
	/**
	 * The default amount of time in milliseconds the data should stay in local storage.
	 * @default 14400000 Default is 4 hours.
	 */
	private expiration = 14400000;
	/**
	 * The prefix to use for the key in local storage.
	 */
	private prefix = '';

	constructor() {
		if (isPlatformServer(inject(PLATFORM_ID))) {
			throw new Error('LocaleStorageService cannot be used on the server');
		}
		const location = inject(PlatformLocation);

		try {
			const testKey = '__accesso_test_key__';
			localStorage.setItem(testKey, testKey);
			localStorage.removeItem(testKey);
			this.isSupported = true;
		} catch {
			this.isSupported = false;
		}

		const queryString = new URLSearchParams(location.search);

		this.setStoragePrefix(
			this.generatePrefix({
				l: getStore(location),
				m: queryString.get('merchant_id'),
				e: queryString.get('emerchant_id'),
				r: queryString.get('reseller_id'),
				rl: queryString.get('reseller_location_id'),
				tf: queryString.get('trades_flag'),
			})
		);
	}

	/**
	 * Override the default of 4 hours the data should stay in local storage.
	 * @param expiration The amount of time in milliseconds the data should stay in local storage. Must be greater than or equal to 15 minutes.
	 */
	setExpiration(expiration: number): void {
		if (Number.isNaN(expiration) || !Number.isInteger(expiration) || expiration < 900000) {
			throw new Error('Expiration must be a number greater than 15 minutes in milliseconds.');
		}
	}

	generatePrefix(attributes: PrimitiveObject): string {
		const keys = Object.keys(attributes);
		if (!keys.length) {
			throw new RangeError('Attributes must have at least one key.');
		}

		return keys.reduce((acc: string, key) => {
			if (attributes[key]) {
				return acc + `_${key}:${attributes[key]}`;
			}
			return acc;
		}, '');
	}

	setStoragePrefix(key: string): void {
		if (!key.length) {
			throw new Error('Key must have a length greater than 0.');
		}
		this.prefix = key;
	}

	add(key: StorageKey, value: PrimitiveType | PrimitiveArray | PrimitiveObject | ComplexPrimitiveObject): void {
		try {
			const id = this.getKey(key);
			const savedValue = JSON.stringify(value);
			if (this.isSupported) {
				this.window.localStorage.setItem(id, savedValue);
			} else {
				this.fallbackStorage.set(id, savedValue);
			}
		} catch (err) {
			this.logger.error(`Error stringifying local storage value for key ${key}: ${err}`);
		}
	}

	get<T>(key: StorageKey): T | null {
		if (!this.has(key)) {
			return null;
		}

		const id = this.getKey(key);

		try {
			let value: string;
			if (this.isSupported) {
				value = this.window.localStorage.getItem(id) as string;
			} else {
				value = this.fallbackStorage.get(id) as string;
			}
			return JSON.parse(value) as T;
		} catch (err) {
			this.logger.error(`Error parsing local storage value for key ${id}: ${err}`);
		}
		return null;
	}

	has(key: StorageKey): boolean {
		const id = this.getKey(key);
		if (this.isSupported) {
			if (id in this.window.localStorage) {
				return true;
			}
			return false;
		}
		return this.fallbackStorage.has(id);
	}

	getKeys(): string[] {
		if (this.isSupported) {
			return keys(this.window.localStorage);
		}
		return Array.from(this.fallbackStorage.keys());
	}

	remove(key: StorageKey): void {
		if (!this.has(key)) {
			return;
		}

		const id = this.getKey(key);
		if (this.isSupported) {
			this.window.localStorage.removeItem(id);
		} else {
			this.fallbackStorage.delete(id);
		}
	}

	clearAll(): void {
		(this.isSupported ? this.window.localStorage : this.fallbackStorage).clear();
	}

	private getKey(key: StorageKey): string {
		return `${this.prefix}_${key}`;
	}
}

export function provideLocalStorageService(): Provider {
	return {
		provide: StorageService,
		useClass: LocalStorageService,
	};
}
